import * as client from "../../../client";
import { formatConstructionDisplay } from "./construction";
import { formatSicDisplay } from "./sic";

const NONE_GIVEN = "NONE GIVEN";

export const originalConstructionDescriptionFromRawAddress = (
  rawAddress: client.Address
) => {
  return (
    rawAddress.private_original_values["construction_description"] ?? NONE_GIVEN
  );
};

export const originalOccupancyDescriptionFromRawAddress = (
  rawAddress: client.Address
) => {
  return (
    rawAddress.private_original_values["occupancy_description"] ??
    rawAddress.private_original_values["location_description"] ??
    rawAddress.private_original_values["location_name"] ??
    NONE_GIVEN
  );
};

const formatOriginalValues = (
  values: { label: string; value: string | undefined }[]
): string => {
  const parts = values.filter((v) => v.value);
  if (parts.length === 0) {
    return "No original value was given";
  }
  return parts.map((v) => `${v.label}: "${v.value}"`).join(", ");
};

const constructionValuesString = (
  values: Record<string, string | null>
): string => {
  return formatOriginalValues([
    { label: "Scheme", value: values["construction_code_type"] ?? undefined },
    {
      label: "Description",
      value: values["construction_description"] ?? undefined,
    },
    { label: "Code", value: values["construction_code"] ?? undefined },
  ]);
};

const occupancyValuesString = (
  values: Record<string, string | null>
): string => {
  return formatOriginalValues([
    { label: "Scheme", value: values["occupancy_code_type"] ?? undefined },
    {
      label: "Description",
      value:
        values["occupancy_description"] ??
        values["location_description"] ??
        values["location_name"] ??
        undefined,
    },
    { label: "Code", value: values["occupancy_code"] ?? undefined },
  ]);
};

const originalConstructionTooltip = (address: client.Address): string =>
  constructionValuesString(address.private_original_values);

const originalOccupancyTooltip = (address: client.Address): string =>
  occupancyValuesString(address.private_original_values);

const computedConstructionDisplay = (address: client.Address): string => {
  const cv = address.private_computed_values;
  return formatConstructionDisplay(
    cv.construction_code_type,
    cv.construction_code,
    cv.construction_description
  );
};

const computedSicDisplay = (address: client.Address): string =>
  formatSicDisplay(address.private_computed_values.sic_code);

type FlagIds = client.Address["private_flags"][string][number];

export const severityForFlag = (flagId: FlagIds): number => {
  if (typeof flagId === "string") {
    switch (flagId) {
      case "date_parsing_error":
        return 65;
      case "missing_coverage_headers":
        return 90;
      case "number_parsing_error":
        return 70;
      case "suspicious_year_warning":
        return 65;
      case "general_mismatch":
        return 70;
      case "large_number_warning":
        return 90;
      case "low_confidence":
        return 81;
      case "medium_confidence":
        return 65;
      case "year_out_of_range_warning":
        return 64;
      case "non_property_insurance_value":
        return 65;
      case "unrecognized_central_alarm_value":
        return 65;
      case "unrecognized_yes_no_value":
        return 65;
      case "unrecognized_sprinkler_value":
        return 65;
      case "low_sprinkler_percentage":
        return 60;
      case "zip_parsing_error":
        return 85;
      case "blank_tiv_value":
        return 90;
      case "address_corrected_by_geocoding":
        return 0;
      case "non_ascii_removed":
        return 60;
      case "geocoding_failed":
        return 90;
      case "location_number_mismatch":
        return 60;
      case "year_defaulted_to_year_built":
        return 60;
      case "approximate_geocoding":
        return 90;
      case "high_protection_class":
        return 90;
      case "tier_1_wind_zone":
        return 90;
      case "itv_calc_warning":
        return 90;
      case "bi_estimate_warning":
        return 60;
      case "coverage_adjusted_by_reconciliation":
        return 90;
      case "zip_unrecognized":
        return 85;
      case "zip_not_rater_valid":
        return 70;
      case "zip_state_mismatch":
        return 90;
      case "number_of_buildings_replaced":
        return 70;
    }
  }

  switch (flagId.flag_id) {
    case "unkonwn_coverage_column":
      return 90;
    case "non_property_insurance_value_v2":
      return 65;
    case "address_corrected_by_llm_parse":
      return 75;
    case "address_flagged_by_geocoding":
      return 75;
    case "coverage_computed_from_column_sum":
      return 90;
  }
};

export const maxSeverityForAddressFlags = (
  address: client.Address,
  addressField: keyof client.RawAddress | undefined
): number => {
  if (addressField === undefined) {
    return 0;
  }
  let maxSeverity = undefined;
  for (const flagId of address.private_flags[addressField] ?? []) {
    const newSeverity = severityForFlag(flagId);

    if (newSeverity > (maxSeverity ?? 0)) {
      maxSeverity = newSeverity;
    }
  }
  return maxSeverity ?? 0;
};

const flagText = (flag: FlagIds, originalValue: string | null): string => {
  const safeOriginalValue = originalValue ?? "NONE GIVEN";

  if (typeof flag === "string") {
    switch (flag) {
      case "date_parsing_error":
        return `Failed to parse date: "${safeOriginalValue}"`;
      case "missing_coverage_headers":
        return "No coverage headers found that sum to the TIV. Defaulting all TIV to Building Value.";
      case "number_parsing_error":
        return `Failed to parse number value: "${safeOriginalValue}"`;
      case "suspicious_year_warning":
        return `Warning when extracting a 4 digit year from: "${safeOriginalValue}"`;
      case "general_mismatch":
        return `The extracted value looks different than the original: "${safeOriginalValue}"`;
      case "large_number_warning":
        return "This number looks too large to be correct.";
      case "low_confidence":
        return `We have low confidence in this value.`;
      case "medium_confidence":
        return `We have medium confidence in this value.`;
      case "year_out_of_range_warning":
        return `This year was adjusted up to 1905 to avoid issues in excel (originally, this value was "${safeOriginalValue}")`;
      case "non_property_insurance_value":
        return "We found a non-property insurance subject for this location in the Acord app.";
      case "unrecognized_central_alarm_value":
        return `Unrecognized alarm type. The original value was "${safeOriginalValue}"`;
      case "unrecognized_sprinkler_value":
        return `Unrecognized sprinkler type. The original value was "${safeOriginalValue}"`;
      case "low_sprinkler_percentage":
        return `We found a low sprinkler percentage. The original value was "${safeOriginalValue}"`;
      case "unrecognized_yes_no_value":
        return `Unrecognized yes/no value. The original value was "${safeOriginalValue}"`;
      case "location_number_mismatch":
        return `We found a mismatch on this location value. The original was "${safeOriginalValue}"`;
      case "zip_parsing_error":
        if (originalValue) {
          return `Failed to parse zip code: "${safeOriginalValue}"`;
        } else {
          return `Zip code not found`;
        }
      case "blank_tiv_value":
        return "A blank tiv value was found so we defaulted to $0. This can throw off other coverage values as we try to find columns that sum to tiv.";
      case "address_corrected_by_geocoding":
        if (originalValue) {
          return `This field was corrected by geocoding. The original value was "${safeOriginalValue}"`;
        } else {
          return `This field was filled by geocoding (not found in input)`;
        }
      case "non_ascii_removed":
        return `Some special (non-ASCII) characters were removed from this field. The original value was "${safeOriginalValue}"`;
      case "geocoding_failed":
        return "We were unable to geocode this address.";
      case "year_defaulted_to_year_built":
        return "No year was found so we defaulted to the year built";
      case "approximate_geocoding":
        return "We geocoded this address, but only found approximate coordinates. This may introduce error on the street or map views.";
      case "high_protection_class":
        return "This protection class is higher than the recommended maximum";
      case "tier_1_wind_zone":
        return "This county is a tier 1 windzone";
      case "itv_calc_warning":
        return "The ITV limit is not close to the requested building value. Further review is needed; one of the ITV inputs may be wrong, or Polaris may have the wrong requested building value.";
      case "bi_estimate_warning":
        return "No BI was found on the original submission. We calculated this estimate by taking 8% of the building + bpp values.";
      case "coverage_adjusted_by_reconciliation":
        return "This coverage value was adjusted during TIV reconciliation to match the expected total.";
      case "zip_unrecognized":
        return `This zip code is not recognized: "${safeOriginalValue}"`;
      case "zip_not_rater_valid":
        return "This zip is not compatible with the rater: a substitute will be used.";
      case "zip_state_mismatch":
        return "This zip does not match the state.";
      case "number_of_buildings_replaced":
        return `The number of buildings looked too large (original was "${safeOriginalValue}"), so we defaulted to 1. This usually means the wrong column was mapped.`;
    }
  }

  switch (flag.flag_id) {
    case "unkonwn_coverage_column":
      return `These column headers don't appear to be related to property insurance, but they were included in the spreadsheet's TIV calculation: ${flag.columns
        .map((column) => `"${column}"`)
        .join(", ")}`;
    case "non_property_insurance_value_v2":
      return `These insurance subjects don't appear to be related to property insurance, so we did not include them in the TIV calculation: ${flag.subjects
        .map((subject) => `"${subject}"`)
        .join(", ")}`;
    case "address_corrected_by_llm_parse":
      return `We have medium confidence in this value, which was extracted from "${flag.original}"`;
    case "address_flagged_by_geocoding":
      return `This address did not match geocoding. The geocoded value "${flag.geocoded}" will be used for rating (original was "${safeOriginalValue}")`;
    case "coverage_computed_from_column_sum":
      return `We were unable to find a TIV column that was the sum of other coverage columns. We calculated TIV by adding the following columns: ${flag.columns
        .map((column) => `"${column}"`)
        .join(", ")}`;
  }
};

export const flagTextFor = (
  address: client.Address,
  addressField: keyof client.RawAddress | undefined,
  showOriginalValueIfNoFlags?: boolean,
  isRequiredAndIsBlank?: boolean
) => {
  if (addressField === undefined) {
    return undefined;
  }

  if (addressField in address.private_overrides) {
    switch (addressField) {
      case "construction_code":
        return `A user edited this value. The previous value was "${computedConstructionDisplay(address)}"\nOriginal values — ${originalConstructionTooltip(address)}`;
      case "sic_code":
        return `A user edited this value. The previous value was "${computedSicDisplay(address)}"\nOriginal values — ${originalOccupancyTooltip(address)}`;
      case "occupancy_code":
        return `A user edited this value. The previous value was "${computedSicDisplay(address)}"\nOriginal values — ${originalOccupancyTooltip(address)}`;
      default: {
        const computedValue =
          address.private_computed_values[addressField] ?? undefined;
        return computedValue
          ? `A user edited this value. The previous value was "${computedValue}"`
          : "A user edited this value";
      }
    }
  }

  let originalValue = address.private_original_values[addressField] ?? "";

  switch (addressField) {
    case "construction_code":
      originalValue = originalConstructionDescriptionFromRawAddress(address);
      break;
    case "occupancy_code":
      originalValue = originalOccupancyDescriptionFromRawAddress(address);
      break;
    case "sic_code":
      // This is what we used as input when getting an sic code
      originalValue = originalOccupancyDescriptionFromRawAddress(address);
      break;
    default:
      break;
  }

  const flagOnlyText = (address.private_flags[addressField] ?? [])
    .map((flag) => flagText(flag, originalValue))
    .concat(isRequiredAndIsBlank ? ["This field is required for rating"] : [])
    .map((text) => `- ${text}`)
    .join("\n");

  if (flagOnlyText === "" && showOriginalValueIfNoFlags) {
    switch (addressField) {
      case "construction_code":
        return `Original values — ${originalConstructionTooltip(address)}`;
      case "sic_code":
      case "occupancy_code":
        return `Original values — ${originalOccupancyTooltip(address)}`;
      default:
        if ((address.private_original_values[addressField] ?? "") === "") {
          return "No original value was given";
        }
        return `The original value was: "${address.private_original_values[addressField]}"`;
    }
  }

  if (flagOnlyText !== "") {
    switch (addressField) {
      case "construction_code":
        return `${flagOnlyText}\n\nOriginal values — ${originalConstructionTooltip(address)}`;
      case "sic_code":
      case "occupancy_code":
        return `${flagOnlyText}\n\nOriginal values — ${originalOccupancyTooltip(address)}`;
      default:
        break;
    }
  }

  return flagOnlyText;
};
